import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';

import AnonMobileSideBar from '~/components/organisms/anon-sidebar/AnonMobileSideBar';
import FooterPublic from '~/components/organisms/FooterPublic/FooterPublic';
import HeaderPublic from '~/components/organisms/HeaderPublic/HeaderPublic';
import { Button } from '~/components/ui/button';
import { PATH } from '~/constants/route-path';
import { COLORS } from '~/styles/colors';
import { isMobileResolution } from '~/utils/common';

export default function Privacy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openBar, setOpenBar] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <AnonMobileSideBar open={openBar} setOpen={setOpenBar}>
        <>
          <HeaderPublic />
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'trasparent',
            }}
          >
            <Box
              sx={{
                top: 0,
                height: '30%',
                width: '100%',
                position: 'absolute',
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                pl: isMobileResolution ? 0 : 2,
                pr: isMobileResolution ? 0 : 2,
                zIndex: 1,
                pt: isMobileResolution ? 6 : 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobileResolution ? 28 : 48,
                  letterSpacing: -0.96,
                  lineHeight: '116%',
                  pt: 10,
                }}
              >
                {t('privacy:title')}
              </Typography>
              <Typography
                sx={{
                  pt: 2,
                  pb: 10,
                  textAlign: 'center',
                  fontSize: 16,
                  letterSpacing: -0.32,
                  lineHeight: '175%',
                  color: COLORS.black06,
                }}
              >
                {t('privacy:lastUpdate')}
              </Typography>
            </Box>
            <Box sx={{ backgroundColor: COLORS.white, width: '100%', height: 'auto', pt: 10, alignItems: 'center' }}>
              <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:text:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:text:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:text:2')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:contactInfo:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:contactInfo:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:contactInfo:1')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:qrService:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:qrService:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06, textDecoration: 'underline' }}>
                  {t('privacy:qrService:subtitle')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:qrService:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:qrService:2')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:qrService:3')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:contract:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:contract:0')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:contract:item:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:contract:1')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:website:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:website:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:website:1')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3, 4, 5, 6].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:website:item:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:website:2')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:website:3')}
                </Typography>
                {[0, 1, 2].map((_, index) => (
                  <Box key={index} sx={{ flexDirection: 'column' }}>
                    <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                      {t(`privacy:website:links:${index}:title`)} {t(`privacy:website:links:${index}:link`)}
                    </Typography>
                  </Box>
                ))}

                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:website:4')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:website:5')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:business:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:business:0')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:business:subtitle1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:business:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:business:2')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:web:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:web:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:web:1')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:customer:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:customer:0')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3, 4, 5, 6].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:customer:item:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:customer:1')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:customer:item1:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:customer:2')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:customer:3')}
                </Typography>
                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:marketing:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:marketing:0')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:marketing:item:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:marketing:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:marketing:2')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:marketing:item2:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:marketing:3')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:gdpr:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:gdpr:0')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3, 4].map((_, index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:gdpr:item:${index}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:gdpr:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06, textDecoration: 'underline' }}>
                  {t('privacy:gdpr:subunder')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:gdpr:2')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:gdpr:3')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:gdpr:4')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:california:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06, textDecoration: 'underline' }}>
                  {t('privacy:california:sub1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:2')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06, textDecoration: 'underline' }}>
                  {t('privacy:california:sub2')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:3')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:4')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06, textDecoration: 'underline' }}>
                  {t('privacy:california:sub3')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:5')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:6')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:7')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:8')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:9')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:10')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:11')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:12')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:13')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:14')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:15')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:16')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:17')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:18')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:19')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:20')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:21')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:22')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:california:23')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:virgina:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:1')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:2')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, listStyleType: 'disc', m: 0 }}>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <Box component="li" key={index} sx={{ mb: 1.5 }}>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06, fontWeight: '600' }}>
                        {t(`privacy:virgina:item:${index}:title`)}
                      </Typography>
                      <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                        {t(`privacy:virgina:item:${index}:desc`)}
                      </Typography>
                      {index === 3 && (
                        <Box component="ul" sx={{ pl: 2, listStyleType: 'revert-layer', m: 0 }}>
                          {[0, 1, 2].map((_, indexSub) => (
                            <Box component="li" key={indexSub}>
                              <Typography variant="body1" component="span" sx={{ color: COLORS.black06 }}>
                                {t(`privacy:virgina:item:${index}:subItems:${indexSub}`)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:3')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:4')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:5')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:6')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:7')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:8')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:9')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:10')}
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.black06, textDecoration: 'underline' }}>
                  {t('privacy:virgina:sub')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:virgina:11')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:children:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:children:0')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:dataprivacy:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:dataprivacy:0')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:dataprivacy:1')}
                </Typography>

                <Box sx={{ mt: 6 }} />

                <Typography variant={isMobileResolution ? 'h5' : 'h4'} gutterBottom sx={{ color: COLORS.black08 }}>
                  {t('privacy:change:title')}
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.black06 }}>
                  {t('privacy:change:0')}
                </Typography>

                <Box sx={{ mt: 6 }} />
              </Container>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 10 }}>
                <Button
                  onClick={() => navigate(PATH.LOGIN)}
                  sx={{
                    backgroundColor: COLORS.purple,
                    color: COLORS.white,
                    padding: '8 22px',
                    mt: 1,
                    '&:hover': {
                      color: COLORS.black,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: COLORS.primaryGray,
                    },
                  }}
                >
                  {t('terms:button')}
                </Button>
              </Box>
            </Box>
          </Box>
          <FooterPublic />
        </>
      </AnonMobileSideBar>
    </>
  );
}
